import { useTranslation } from "react-i18next";
import { Desktop, DesktopComponentRegistry } from "../../common/components/Desktop";
import { cdate, now } from "../../common/utils/CDate";
import { StatsContext, useStatsContext } from "./JobStatsContext";
import useClientConfig from "../../common/hooks/clientConfigHook";
import { useEffect, useMemo, useState } from "react";
import { PCRStatsList } from "./PCRStatsList";
import { ResizeAwareDiv } from "../../common/components/ResizeablePanel";
import { dynamicSort } from "../../common/utils/tableUtilities";
import { toFullOption } from "react-querybuilder";
import { useEpcrHistory } from "../../reducers/epcrSlice";
import StatsDesktop from "./StatsDesktop";
import { DateRangeDialog } from "../../common/components/DateRangeDialog";
import { Col, Row } from "../../common/components/Grid";
import Button from "../../common/components/Button";
import Icon from "../../common/components/Icon";
import useWebsocket from "../../common/hooks/websocketHook";
import { buildColumns } from "../epcr/epcrUtilities";
import { useNetworkState } from "../../common/hooks/appStateHook";

const PCRStatsDisplay = () => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const { search, setSearch } = useStatsContext();
    const [dateRange, setDateRange] = useState();
    const { wsStatus } = useNetworkState();
    const { emit } = useWebsocket();
    const fields = useMemo(() => {
        const columns = buildColumns(t, clientConfig);
        for (const cell of columns.filter(f => f.type === 'datetime')) {
            columns.push({ ...cell, type: 'number', field: cell.field + 'Hour', searchLabel: (cell.searchLabel ?? cell.title ?? cell.label) + ' Hour' });
        }
        const result = columns.map(cell => toFullOption({ ...cell, name: cell.field, label: (cell.section ? cell.section + ' - ' : '') + (cell.searchLabel ?? cell.title ?? cell.label) }))
        result.sort(dynamicSort(['label']));
        return result; 
    }, [t, clientConfig]);
    const rawPcrs = useEpcrHistory(search.creationAfter, search.creationBefore);
    const data = useMemo(() => rawPcrs.map(pcr => {
        const result = { ...pcr };
        for (const col of fields.filter(c => c.type === 'datetime')) {
            if (pcr[col.field]) {
                result[col.field + 'Hour'] = cdate(pcr[col.field]).hours();
            }
        }
        return result;
    }), [rawPcrs, fields]);

    useEffect(() => {
        emit('connectIndependentPCR', {});
        return () => emit('disconnectIndependentPCR', {});
    }, [emit, wsStatus]);

    return <StatsDesktop data={data} fields={fields} personalLayoutKey="pcrStatsLayoutsPersonal" defaultLayoutKey="pcrStatsLayoutsDefault" headerControls={<>
        <DateRangeDialog show={!!dateRange} start={dateRange?.creationAfter} end={dateRange?.creationBefore} onClose={() => setDateRange()} onSave={(start, end, tz) => { setSearch(search => ({ ...search, creationAfter: start, creationBefore: end, tz })); setDateRange(); }}></DateRangeDialog>
        <Row>
            <Col>
                <h4>{t('stats')} {cdate(search.creationAfter, clientConfig.tz).dateTimeFormat(clientConfig.timezones?.enable)} - {cdate(search.creationBefore, clientConfig.tz).dateTimeFormat(clientConfig.timezones?.enable)}</h4>
            </Col>
            <Col xs="auto">
                <Button id="epcrStatsDateRange" variant="default" onClick={() => setDateRange({ creationAfter: search.creationAfter, creationBefore: search.creationBefore })}><Icon variant="solid" kind="calendar-week"></Icon> {t('dateRange')}</Button>
            </Col>
        </Row>
    </>}></StatsDesktop>
}

const PCRStatsDisplayWrapper = () => {
    return <ResizeAwareDiv>
        <PCRStatsDisplay></PCRStatsDisplay>
    </ResizeAwareDiv>;
}

const layouts = [
    {
        id: 0,
        title: '1 Column',
        defaultNarrow: true,
        columns: [
            { rows: [{ id: 'main', components: ['pcrStatsDisplayWrapper'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 100, id: 1}
        ]
    },
    {
        id: 1,
        title: '2 Column Left',
        narrow: false,
        defaultMedium: true,
        defaultWide: true,
        columns: [
            { rows: [{ id: 'left', components: ['pcrStatsList'], proportion: 100 }], proportion: 25, id: 2},
            { rows: [{ id: 'main', components: ['pcrStatsDisplayWrapper'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 75, id: 1 }
        ]
    }
]

const PCRStats = () => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const [search, setSearch] = useState({ creationAfter: now(clientConfig.tz).startOf('day').add(4, 'hour').toISOString(), creationBefore: now(clientConfig.tz).add(1, 'day').startOf('day').add(4, 'hour').toISOString() });
    //const [search, setSearch] = useState({ creationAfter: now(clientConfig.tz).startOf('day').add(-10, 'years').toISOString(), creationBefore: now(clientConfig.tz).add(1, 'day').startOf('day').add(4, 'hour').toISOString() });
    
    const components = useMemo(() => {
        const registry = new DesktopComponentRegistry();
        registry.addList({ id: 1, title: t('tools'), icon: { variant: 'solid', kind: 'screwdriver-wrench' } });
        registry.register({ sid: 'pcrStatsList', component: PCRStatsList, label: t('epcrList'), icon: { variant: 'solid', kind: 'table-list' }, primary: true, list: 1 });
        registry.register({ sid: 'pcrStatsDisplayWrapper', component: PCRStatsDisplayWrapper, label: t('stats'), icon: { variant: 'solid', kind: 'chart-simple' }, primary: true, list: 1 })
        return registry;
    }, [t]);

    const context = useMemo(() => ({ search, setSearch }), [search]);

    return <div className="pt-2 h-100">
        <StatsContext.Provider value={context}>
            <Desktop id="pcrStats" key="desktop" layouts={layouts} components={components}></Desktop>
        </StatsContext.Provider>
    </div>
}

export default PCRStats;